import * as Helpers from "./helpers";
import Typography from "@portal/components/atoms/v2/typography";
import usePortfolios from "@portal/hooks/usePortfolios";
import useBankAccounts from "@portal/hooks/useBankAccounts";
import useCustomerInfo from "@portal/hooks/useCustomerInfo";
import { useRouter } from "next/navigation";
import { useEffect, useState, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@portal/redux/store";
import { MainBox } from "../components/mainBox";
import { OnAdvisorChange } from "../components/handlers";
import { PeriodDayOptions, AccountOptions, AdvisorOptions, Footer, NameInputOptions } from "./components";
import { IPageProps } from "@portal/types/robot/IPageProps";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { InfoBoxComponent } from "@portal/components/atoms/infoBox";
import { TextLine } from "@portal/components/atoms/v2/textLine";
import { IModifySavingsAgreement } from "@portal/types/api/IModifySavingsAgreement";
import { ICreateSavingsAgreement } from "@portal/types/api/ICreateSavingsAgreement";
import { CreateAskDialog } from "../../ask/create/createAskDialog";
import { setSuitabilityReport, setYearlyIncreasePercent } from "@portal/redux/reducers/ui/createSavingsAgreement";
import UseCreateSuitabilityReport from "@portal/hooks/useCreateSuitabilityReport";
import YearlyIncrease, { getYearlyIncreaseIndex } from "../../agreements/shared/yearlyIncrease";
import Input from "@portal/components/atoms/v2/input";

export const CompleteSavingsPage = ({ isDefaultLanguage, centerId }: IPageProps) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;
  const [isLoading, setIsLoading] = useState(false);
  const { request } = useAppSelector(state => state.persisted.savingsAgreement);
  const data = request.agreementData;
  const [showCreateAskModal, setShowCreateAskModal] = useState(false);
  const [advisorCarriedOut, setAdvisorCarriedOut] = useState(data.advisorCarriedOut);
  const [agreementName, setAgreementName] = useState(data.adjustmentComment ?? "");
  const [selectedPeriodDay, setSelectedPeriodDay] = useState<number>(Helpers.MapPeriodDayToIndex(data.periodDayNumber ?? 0));
  const [primaryButtonDisabled, setPrimaryButtonDisabled] = useState(true);
  const [selectedItem, setSelectedItem] = useState(getYearlyIncreaseIndex(request.agreementData.yearlyIncreasePercent ?? 0));

  const { data: portfolios } = usePortfolios();
  const savingCalculations = useAppSelector(state => state.persisted.savingCalculations.request);
  const riskLevelRequest = useAppSelector(state => state.persisted.riskLevel.request);
  const riskLevelResponse = useAppSelector(state => state.persisted.riskLevel.response);
  const isAskEnabled = Helpers.IsAskEnabled(riskLevelResponse.data);

  const { bankAccounts } = useBankAccounts();
  const filteredPortfolios = useMemo(
    () => (portfolios ? Helpers.FilterPortfoliosByCenterId(portfolios, centerId) : []),
    [centerId, portfolios],
  );
  const selectedPortfolio = useMemo(
    () => filteredPortfolios.find(pf => pf.portfolioId === request.portfolioId && (pf.taxationType !== "ASK" || isAskEnabled)),
    [filteredPortfolios, isAskEnabled, request.portfolioId],
  );

  const { data: customerInfo } = useCustomerInfo();
  const { submitSuitabilityReport } = UseCreateSuitabilityReport();
  const suitabilityReport = Helpers.GetSuitabilityReport(
    request,
    isDefaultLanguage,
    savingCalculations,
    riskLevelRequest,
    customerInfo,
  );

  useEffect(() => {
    if (IsValidRequest(data, request, advisorCarriedOut)) {
      setPrimaryButtonDisabled(false);
    } else {
      setPrimaryButtonDisabled(true);
    }
  }, [advisorCarriedOut, data, request]);

  const handleYearlyIncreaseChange = (i: number, percent: number) => {
    dispatch(setYearlyIncreasePercent(percent));
    setSelectedItem(i);
  };

  const createSuitabilityReport = async () => {
    setIsLoading(true);
    const response = await submitSuitabilityReport(suitabilityReport);
    if (response?.text) dispatch(setSuitabilityReport(response?.text));
    setIsLoading(false);
    router.push(`/robot/17`);
  };

  return (
    <MainBox>
      <TextLine />
      <NameInputOptions
        name={agreementName ?? ""}
        header={texts.PAGE_16_NAME}
        setAgreementName={setAgreementName}
        dispatch={dispatch}
      />
      <PeriodDayOptions
        isDefaultLanguage={isDefaultLanguage}
        selectedPeriodDayNumber={selectedPeriodDay}
        header={texts.PAGE_16_DATE}
        setSelectedPeriodDayNumber={setSelectedPeriodDay}
        dispatch={dispatch}
      />
      {!isAskEnabled && <InfoBoxComponent texts={[texts.PAGE_16_INFO_BOX]} />}
      <AccountOptions
        selectedPortfolio={selectedPortfolio}
        bankAccounts={bankAccounts}
        selectedBankAccountNumber={data.cashAccount}
        otherBankAccountNumber={data.otherCashAccount}
        isDefaultLanguage={isDefaultLanguage}
        isAskDisabled={!isAskEnabled}
        dispatch={dispatch}
        createAskAccount={isAskEnabled}
        setShowCreateAskModal={setShowCreateAskModal}
        showOtherBankAccount={savingCalculations.monthlyAmount > 0 && savingCalculations.oneTimeDeposit > 0}
      />
      <div className="my-5">
        <AdvisorOptions
          isDefaultLanguage={isDefaultLanguage}
          advisorCarriedOut={advisorCarriedOut}
          setAdvisorCarriedOut={setAdvisorCarriedOut}
          dispatch={dispatch}
        />
        {advisorCarriedOut && (
          <div className="flex flex-col mt-2">
            <Typography variant="Body">{texts.PAGE_16_ADVISOR}</Typography>
            <div className="border border-blueLight rounded-md">
              <Input
                type="text"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => OnAdvisorChange(e.target.value, dispatch)}
                value={request.agreementData.advisorName}
              />
            </div>
          </div>
        )}
      </div>
      <div className="mb-2">
        <YearlyIncrease handleChange={handleYearlyIncreaseChange} selectedItem={selectedItem} />
      </div>
      <Footer
        isDefaultLanguage={isDefaultLanguage}
        buttonDisabled={primaryButtonDisabled}
        router={router}
        onClick={() => createSuitabilityReport()}
        isLoading={isLoading}
      />
      {showCreateAskModal && (
        <CreateAskDialog centerId={centerId} isDefaultLanguage={isDefaultLanguage} setShowAskComponent={setShowCreateAskModal} />
      )}
    </MainBox>
  );
};

function IsValidRequest(data: IModifySavingsAgreement, request: ICreateSavingsAgreement, advisorCarriedOut: boolean) {
  return data.cashAccount && request.portfolioId && advisorCarriedOut !== null;
}
